import React, { useState } from "react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import useReveal from "../Components/useReveal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faClock,
  faCalendarCheck,
  faShieldHeart,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../Styles/Forms.css";
import asideImg from "../Images/image4.jpg";

function BookAppointment() {
  useReveal();
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formEl = e.target;
    const payload = Object.fromEntries(new FormData(formEl).entries());
    setSending(true);
    try {
      const res = await fetch("/api/appointment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      toast.success(
        "Your appointment request has been received! We'll confirm your preferred time shortly.",
        { position: "top-center" }
      );
      formEl.reset();
    } catch {
      toast.error("Something went wrong. Please try again or call us.", {
        position: "top-center",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="book-page">
      <Navbar />
      <ToastContainer />

      <section className="page-banner">
        <div className="hr-container">
          <span className="hr-eyebrow">Get Started</span>
          <h1>Book An Appointment</h1>
          <p>
            Take the first step toward compassionate care. Share your details
            and we'll reach out to confirm your appointment.
          </p>
          <div className="page-crumb">
            <a href="/">Home</a> / Book An Appointment
          </div>
        </div>
      </section>

      <section className="hr-section">
        <div className="hr-container">
          <div className="form-layout">
            {/* Aside */}
            <aside className="form-aside reveal">
              <div className="form-aside-photo">
                <img src={asideImg} alt="Caregiver assisting a client" />
              </div>
              <div className="form-aside-inner">
              <h3>We're Here to Help</h3>
              <p>
                Prefer to talk to someone? Reach out any time and our care team
                will guide you through the next steps.
              </p>
              <div className="form-aside-item">
                <span className="fa-icon">
                  <FontAwesomeIcon icon={faPhone} />
                </span>
                <div>
                  <span>Call Us</span>
                  <a href="tel:2535230098">253-523-0098</a>
                </div>
              </div>
              <div className="form-aside-item">
                <span className="fa-icon">
                  <FontAwesomeIcon icon={faEnvelope} />
                </span>
                <div>
                  <span>Email</span>
                  <a
                    href="mailto:info@harmonyrisehomecare.com"
                    className="form-aside-email"
                  >
                    info@harmonyrisehomecare.com
                  </a>
                </div>
              </div>
              <div className="form-aside-item">
                <span className="fa-icon">
                  <FontAwesomeIcon icon={faClock} />
                </span>
                <div>
                  <span>Availability</span>
                  <strong>6:00 AM – 10:30 PM</strong>
                </div>
              </div>
              <div className="form-aside-item">
                <span className="fa-icon">
                  <FontAwesomeIcon icon={faShieldHeart} />
                </span>
                <div>
                  <span>Your Privacy</span>
                  <strong>Kept confidential</strong>
                </div>
              </div>
              </div>
            </aside>

            {/* Form */}
            <form className="form-card reveal" onSubmit={handleSubmit}>
              {/* Personal */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <span className="fs-num">1</span> Your Details
                </h3>
                <div className="form-grid grid-2">
                  <div className="field">
                    <label>
                      First Name <span className="req">*</span>
                    </label>
                    <input name="firstName" type="text" required placeholder="First name" />
                  </div>
                  <div className="field">
                    <label>
                      Last Name <span className="req">*</span>
                    </label>
                    <input name="lastName" type="text" required placeholder="Last name" />
                  </div>
                </div>
                <div className="form-grid grid-2" style={{ marginTop: "20px" }}>
                  <div className="field">
                    <label>
                      Email <span className="req">*</span>
                    </label>
                    <input name="email" type="email" required placeholder="you@example.com" />
                  </div>
                  <div className="field">
                    <label>
                      Phone <span className="req">*</span>
                    </label>
                    <input name="phone" type="tel" required placeholder="253-523-0098" />
                  </div>
                </div>
                <div className="form-grid grid-2" style={{ marginTop: "20px" }}>
                  <div className="field">
                    <label>
                      Date of Birth <span className="req">*</span>
                    </label>
                    <input name="dob" type="date" required />
                  </div>
                  <div className="field">
                    <label>
                      Gender <span className="req">*</span>
                    </label>
                    <select name="gender" required defaultValue="">
                      <option value="" disabled>
                        Select gender
                      </option>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <span className="fs-num">2</span> Address
                </h3>
                <div className="form-grid">
                  <div className="field">
                    <label>Address</label>
                    <input name="address" type="text" placeholder="Street address" />
                  </div>
                </div>
                <div className="form-grid grid-3" style={{ marginTop: "20px" }}>
                  <div className="field">
                    <label>City</label>
                    <input name="city" type="text" placeholder="City" />
                  </div>
                  <div className="field">
                    <label>State</label>
                    <input name="state" type="text" placeholder="State" />
                  </div>
                  <div className="field">
                    <label>Postal Code</label>
                    <input name="postalCode" type="text" placeholder="ZIP" />
                  </div>
                </div>
              </div>

              {/* Appointment */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <span className="fs-num">3</span> Appointment Details
                </h3>
                <div className="form-grid grid-2">
                  <div className="field">
                    <label>
                      Select Service <span className="req">*</span>
                    </label>
                    <select name="service" required defaultValue="">
                      <option value="" disabled>
                        Choose a service
                      </option>
                      <option>Personal Care Services</option>
                      <option>Companion Care</option>
                      <option>Dementia Care</option>
                      <option>Life Care Services</option>
                    </select>
                  </div>
                  <div className="field">
                    <label>
                      Have you been with us before?{" "}
                      <span className="req">*</span>
                    </label>
                    <div className="choice-row">
                      <label className="choice">
                        <input type="radio" name="returning" value="Yes" required /> Yes
                      </label>
                      <label className="choice">
                        <input type="radio" name="returning" value="No" /> No
                      </label>
                    </div>
                  </div>
                </div>
                <div className="form-grid grid-2" style={{ marginTop: "20px" }}>
                  <div className="field">
                    <label>
                      Appointment Date <span className="req">*</span>
                    </label>
                    <input name="apptDate" type="date" required />
                    <p className="field-hint">
                      Preferred date may vary upon caregiver availability.
                    </p>
                  </div>
                  <div className="field">
                    <label>
                      Preferred Time <span className="req">*</span>
                    </label>
                    <input name="apptTime" type="time" required />
                    <p className="field-hint">
                      We are available between 6:00 AM and 10:30 PM.
                    </p>
                  </div>
                </div>
                <div className="form-grid" style={{ marginTop: "20px" }}>
                  <div className="field">
                    <label>Description</label>
                    <textarea name="description" placeholder="Tell us about your care needs..." />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="hr-btn hr-btn-primary hr-btn-lg form-submit"
                disabled={sending}
              >
                <FontAwesomeIcon icon={faCalendarCheck} />{" "}
                {sending ? "Submitting..." : "Request Appointment"}
              </button>
              <p className="form-note">
                <FontAwesomeIcon icon={faPaperPlane} /> We'll contact you to
                confirm your appointment time.
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default BookAppointment;
